import { useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import cx from 'classnames';

import { accountAuthGuard } from './-account-auth-guard';
import styles from './achievements.module.scss';
import firstWinImage from './imgs/first-win.webp';
import loginStreak30Image from './imgs/login-streak-30.webp';
import loginStreak60Image from './imgs/login-streak-60.webp';
import quickWinImage from './imgs/quick-win.webp';
import oneOnOneWinImage from './imgs/one-on-one-win.webp';

import { LayoutCard } from '~/components/LayoutCard/LayoutCard';
import { AccountLayout } from '~/components/Account/AccountLayout';
import { resolveAchievementContent } from '~/lib/achievementLocale';
import { UserService } from '~/services/userService';

const userService = new UserService();

const ACHIEVEMENT_IMAGES: Record<string, string> = {
  'first-win': firstWinImage,
  'quick-win': quickWinImage,
  'one-on-one-win': oneOnOneWinImage,
  'login-streak-30': loginStreak30Image,
  'login-streak-60': loginStreak60Image,
};

export const Route = createFileRoute('/account/achievements')({
  component: AchievementsPage,
  beforeLoad: ({ context }) => accountAuthGuard({ context }),
});

function AchievementsPage() {
  const { i18n, t } = useTranslation('account');
  const { data: achievementsData } = useSuspenseQuery({
    queryKey: ['user', 'achievements'],
    queryFn: () => userService.achievements(),
  });

  const achievements = achievementsData?.data ?? [];

  const formatDate = (date?: Date): string => {
    if (!date) return '-';
    return new Intl.DateTimeFormat(i18n.language, {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    }).format(new Date(date));
  };

  return (
    <AccountLayout>
      <LayoutCard title={t('achievements.title')}>
        <div className={styles.grid}>
          {achievements.map((achievement) => {
            const isLocked = !achievement.enabled || !achievement.earnedAt;
            const localized = resolveAchievementContent(t, achievement.id, {
              name: achievement.name,
              description: achievement.description,
            });

            return (
              <div
                className={cx(styles.card, isLocked && styles.locked)}
                key={achievement.id}
              >
                <div className={styles.imageWrapper}>
                  <img
                    alt={localized.name}
                    className={styles.image}
                    src={ACHIEVEMENT_IMAGES[achievement.id]}
                  />
                  {isLocked && <div className={styles.lockOverlay} />}
                </div>
                <div className={styles.content}>
                  <h3 className={styles.name}>{localized.name}</h3>
                  <p className={styles.description}>{localized.description}</p>
                  <div className={styles.footer}>
                    {isLocked ? (
                      <span className={styles.status}>
                        {t('achievements.locked')}
                      </span>
                    ) : (
                      <span className={styles.date}>
                        {t('achievements.earned', {
                          date: formatDate(achievement.earnedAt),
                        })}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        {achievements.length === 0 && (
          <div className={styles.empty}>{t('achievements.empty')}</div>
        )}
      </LayoutCard>
    </AccountLayout>
  );
}
